<template>
  <div :class="backgroundColorClass">
    <div class="program-overview">
      <h2
        class="program-overview__header"
        v-html="$t('b2b__landing_page__program_overview__title')"
      />
      <div
        class="program-overview__intro"
        v-html="$t('b2b__landing_page__program_overview__description')"
      />
      <div class="program-overview-sections">
        <div
          v-for="(card, index) in cards"
          :key="index"
          class="program-overview-section"
        >
          <div
            :class="['program-overview-section__banner', card.class]"
            v-html="card.text"
          />
          <div
            class="program-overview-section__info"
            v-html="card.description"
          />
        </div>
      </div>
      <BaseButton
        class="cta"
        @click="handleCTAButtonClick"
      >
        {{ ctaBtnTitle }}
      </BaseButton>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'petite-vue-i18n'
import BaseButton from '@shared/components/BaseButton.vue'

const { t } = useI18n()

defineProps({
  ctaBtnTitle: {
    type: String,
    default: '',
  },
  handleCTAButtonClick: {
    type: Function,
    default: () => {},
  },
  backgroundColorClass: {
    type: String,
    default: 'kds-color-background-base',
  },
})

const cards = [
  {
    class: 'program-overview-section__banner--coaching',
    text: t('b2b__landing_page__generic__coaching'),
    description: t(
      'b2b__landing_page__program_overview__coaching__description',
    ),
  },
  {
    class: 'program-overview-section__banner--exercise',
    text: t('b2b__landing_page__generic__exercise'),
    description: t(
      'b2b__landing_page__program_overview__exercise__description',
    ),
  },
  {
    class: 'program-overview-section__banner--relaxation',
    text: t('b2b__landing_page__generic__relaxation'),
    description: t(
      'b2b__landing_page__program_overview__relaxation__description',
    ),
  },
  {
    class: 'program-overview-section__banner--learning',
    text: t('b2b__landing_page__generic__learning'),
    description: t(
      'b2b__landing_page__program_overview__learning__description',
    ),
  },
]
</script>

<style lang="scss" scoped>
.program-overview {
  padding: 31px $kds-space-3x 49px;

  &__header {
    margin: 0;
    padding-bottom: 13px;
    text-align: center;

    @include kds-typography-display-small;
  }

  &__intro {
    text-align: center;

    @include kds-typography-paragraph-large;
  }

  &-section {
    padding-top: 49px;

    &__banner {
      @include kds-typography-title-medium;
      display: block;
      margin: 0 auto;
      height: 275px;
      padding-top: $kds-space-3x;
      box-sizing: border-box;
      background-position: center top;
      background-repeat: no-repeat;
      background-size: auto 100%;
      text-align: center;

      &--coaching {
        color: $kds-color-text-primary-inverse;
        padding-left: 43px;

        background-image: url('@shared/assets/img/coaching_4x.png');
      }

      &--exercise {
        background-image: url('@shared/assets/img/exercise_4x.png');
      }

      &--relaxation {
        background-image: url('@shared/assets/img/relaxation_4x.png');
      }

      &--learning {
        background-image: url('@shared/assets/img/learning_4x.png');
      }
    }

    &__info {
      @include kds-typography-paragraph-large;

      text-align: center;
      padding-top: $kds-space-2_5x;
    }
  }
}

.program-overview {
  @include media-up(sm) {
    padding: $kds-space-6x $kds-space-3x;
  }

  @include media-up(md) {
    padding: 72px 54px;
  }

  @include media-up(lg) {
    padding: 72px 82px;
  }

  @include media-up(xl) {
    padding: $kds-space-12x 82px;
  }

  @include media-up(xxl) {
    max-width: 1200px;
    margin: 0 auto;
  }

  &__header {
    @include media-up(md) {
      @include kds-typography-display-medium;
      padding-bottom: 18px;
    }

    @include media-up(xl) {
      @include kds-typography-display-large;
      padding-bottom: $kds-space-3x;
    }
  }

  &__intro {
    @include media-up(md) {
      @include kds-typography-display-small;
      padding: 0 70px;
    }

    @include media-up(lg) {
      padding: 0 150px;
    }
  }

  &-sections {
    @include media-up(md) {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-around;
    }

    @include media-up(xl) {
      justify-content: space-between;
    }
  }

  &-section {
    @include media-up(md) {
      padding-top: 36px;
      flex-basis: 50%;
    }

    @include media-up(xl) {
      padding-top: $kds-space-6x;
      width: 254px;
      flex-basis: auto;
      padding-bottom: 0;
    }

    &__banner {
      @include media-up(md) {
        @include kds-typography-title-large;
        height: 354px;
        padding-top: 33px;
      }

      @include media-up(xl) {
        width: 100%;
      }

      &--coaching {
        @include media-up(md) {
          padding-left: 60px;
        }
      }
    }

    &__info {
      @include media-up(md) {
        @include kds-typography-paragraph-large;
        padding: 18px 15px 0;
      }

      @include media-up(lg) {
        padding: 18px 65px 0;
      }

      @include media-up(xl) {
        padding: $kds-space-3x 0 0;
      }
    }
  }

  .cta {
    margin: 40px auto 0;
    width: fit-content;
  }
}
</style>
